import axios, { AxiosError } from 'axios';
import {
  Company,
  Resource,
  Event,
  ApiResponseWithPaging,
  BaseResourcesListFilter,
  ResourcesListFilter,
  UpsertEventParams,
  SuperApiTask,
  MultiSelectOption,
  States,
  QualifiedResource,
  Counties,
  GeoAuthorization,
  BulkUpdatePostBody,
  CapacityFilter,
  CapacityFilterResponse,
  State,
  QualifiedResourcePost,
  QualifiedResourcesFNOLFilters,
  ResourcesFilters,
  ResourceFilterGroup,
  GeoResolverResponse,
  GeoResolverError,
} from '@dispatch/shared/types';
import type { GroupAttributeMatchMode } from '@dispatch/shared/types';
import { dispatchClient } from '../clients';
import invariant from 'tiny-invariant';
import { mapResponseWithPaging } from '../helpers/AxiosMappers';
import { addMinutes, isEqual } from 'date-fns';
import { createDateWithTimeZoneOffset } from '@dispatch/shared';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { GridSortItem } from '@mui/x-data-grid-pro';
import { parseSort } from '../tasks/helpers';

const APIM_SERVICE_RESOURCE_SERVICE_URL = 'dispatch/resourceService';
const APIM_SERVICE_RESOURCE_SYNC_URL = `dispatch/resourcesync`;
const APIM_SERVICE_RESOURCES_URL = `${APIM_SERVICE_RESOURCE_SERVICE_URL}/Resources`;
const APIM_SERVICE_RESOURCES_BULK_RESOURCES_URL = `${APIM_SERVICE_RESOURCE_SYNC_URL}/resources/bulkresources`;
const APIM_SERVICE_COMPANY_URL = `${APIM_SERVICE_RESOURCE_SERVICE_URL}/company`;
const APIM_SERVICE_COMPANY_UPSERT_URL = `${APIM_SERVICE_RESOURCE_SERVICE_URL}/company`;
const APIM_SERVICE_CAPACITY_FILTER_URL = `resourceCapacity/filters`;
const APIM_SERVICE_QUALIFIED_RESOURCES_POST = `dispatch/service/tasks/qualifiedResources?pageSize=50`;
const APIM_SERVICE_QUALIFIED_RESOURCES = (taskId: string) =>
  `dispatch/service/tasks/${taskId}/qualifiedResources?pageSize=50`;
const APIM_SERVICE_EFFICIENCIES_URL = (tenantId: string) =>
  `configuration/${tenantId}/efficiencies`;
const APIM_SERVICE_STATES_URL = (tenantId: string) =>
  `configuration/${tenantId}/us-states`;
const APIM_SERVICE_COUNTIES_URL = `georesolver/counties`;
const APIM_SERVICE_GEO_REGIONS_URL = (postalCode: string) =>
  `georesolver/regions/${postalCode}`;
const APIM_LOCATION_BASED_EVENTS_URL = (tenantId: string) =>
  `configuration/${tenantId}/locationBasedEvents`;
const APIM_SERVICE_EXCLUDE_CERTS_AND_LICENSES = (tenantId: string) =>
  `configuration/${tenantId}/feature:excludeCertsAndLicenses`;
const APIM_SERVICE_SET_TASK_UNASSIGNED = (taskId: string) =>
  `/dispatch/service/tasks/${taskId}/status/unassigned`;

type Task = z.infer<typeof SuperApiTask>;

export async function putGeoAuthorization(data: {
  resourceId: string;
  geoAuthorization: GeoAuthorization;
}) {
  const response = await dispatchClient.put<void>(
    `${APIM_SERVICE_RESOURCES_URL}/${data.resourceId}/geoAuthorization/${data.geoAuthorization.id}`,
    {
      geoAuthorizationLevel: data.geoAuthorization.geoAuthorizationLevel,
    }
  );

  return response;
}

export async function getCompany(): Promise<Company> {
  const response = await axios.get<Company>(APIM_SERVICE_COMPANY_URL);
  const company: Company = response?.data;

  return company;
}

export async function upsertCompany(company: Partial<Company>) {
  try {
    await dispatchClient.put<Company>(APIM_SERVICE_COMPANY_UPSERT_URL, company);

    return { success: true };
  } catch (e) {
    return handleError(e as AxiosError | Error, 'Failed to create company');
  }
}

export async function getResourcesPaginated(
  filters: ResourcesListFilter,
  continuationToken?: string,
  sorting?: GridSortItem | null
): Promise<ApiResponseWithPaging<Resource[]>> {
  try {
    const queryFilters = { pageSize: filters.pageSize };

    if (filters.resourceName) {
      filters.name = String(filters.resourceName);
    }

    if (sorting) {
      const sort = parseSort(sorting);
      if (sort) {
        filters = { ...filters, ...sort };
      }
    }

    const response = await dispatchClient.post<Resource[]>(
      `${APIM_SERVICE_RESOURCES_URL}`,
      {
        ...filters,
        resourceCounties: filters.resourceCounties?.map((rc) => rc.value),
      },
      {
        params: {
          ...queryFilters,
        },
        headers: {
          'Continuation-Token': continuationToken || '',
        },
      }
    );

    return mapResponseWithPaging(response);
  } catch (e) {
    /* eslint-disable no-console */
    console.error('error fetching resources from API', e);
    throw new Error('Error fetching resources');
  }
}

export async function getResources(
  filters?: BaseResourcesListFilter
): Promise<Resource[] | undefined> {
  try {
    const response = await axios.get<Resource[]>(
      `${APIM_SERVICE_RESOURCES_URL}`,
      {
        params: {
          ...filters,
        },
      }
    );
    if (response.status === 200 && response?.data) {
      return response.data;
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error('error fetching resources from API', e);
  }
  return undefined;
}

export async function upsertGroupAttributes(payload: {
  id: string;
  attributeGroupName: string;
  tenantId: string;
  docType: string;
  groupMap: Record<string, Record<string, string>>;
  valueMatchModes?: Record<string, GroupAttributeMatchMode>;
}): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await dispatchClient.put(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/attribute-groups`,
      payload,
      {
        headers: {
          'Sys-TenantID': payload.tenantId,
        },
      }
    );

    if (response.status === 200 || response.status === 201) {
      return {
        success: true,
        message: 'Group attributes updated successfully',
      };
    } else {
      return { success: false, message: 'Failed to update group attributes' };
    }
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message || 'Failed to update group attributes';
    return { success: false, message: errorMessage };
  }
}

export async function getGroupAttributes(
  tenantId: string
): Promise<any[] | undefined> {
  if (!tenantId) {
    return undefined;
  }

  try {
    const response = await dispatchClient.get(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/attribute-groups`,
      {
        headers: {
          'Sys-TenantID': tenantId,
        },
      }
    );

    if (response.status === 200 && response?.data) {
      return response.data;
    }
  } catch (e: any) {
    /* eslint-disable no-console */
    console.error('error fetching group attributes from API', e);
  }
  return undefined;
}

export async function deleteGroupAttributes(
  groupId: string,
  tenantId: string
): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await dispatchClient.delete(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/attribute-groups/${groupId}`,
      {
        headers: {
          'Sys-TenantID': tenantId,
        },
      }
    );

    if (response.status === 204 || response.status === 200) {
      return {
        success: true,
        message: 'Group attributes deleted successfully',
      };
    } else {
      return { success: false, message: 'Failed to delete group attributes' };
    }
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message || 'Failed to delete group attributes';
    return { success: false, message: errorMessage };
  }
}

export async function getQualifiedResources(
  taskId?: string
): Promise<QualifiedResource[] | undefined> {
  try {
    if (!taskId) return [];
    const response = await axios.get<QualifiedResource[]>(
      `${APIM_SERVICE_QUALIFIED_RESOURCES(taskId)}`
    );
    const apiResources: QualifiedResource[] = response?.data;

    if (response.status === 200 && apiResources) {
      return apiResources;
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error('error fetching resources from API', e);
  }
  return undefined;
}

export async function getResource(id: string): Promise<Resource | undefined> {
  try {
    const response = await axios.get<Resource>(
      `${APIM_SERVICE_RESOURCES_URL}/${id}`
    );

    const resource: Resource = response?.data;

    if (response.status === 200 && resource) {
      return resource;
    }
  } catch (e) {
    throw new Error('error fetching resources from API');
  }
  return undefined;
}

export async function getResourceEvents(resourceId: string): Promise<Event[]> {
  invariant(resourceId, 'Missing ResourceId, cannot fetch events');
  const res = await dispatchClient.get(
    `/dispatch/resourceService/resources/${resourceId}/events`
  );
  return res.data;
}

export async function getEvent(eventId: string): Promise<Event> {
  invariant(eventId, 'Missing EventId, cannot fetch event');
  const res = await dispatchClient.get<Event>(
    `/dispatch/resourceService/events/${eventId}`
  );
  return res.data;
}

export async function upsertEvent(event: UpsertEventParams) {
  return upsertEvents([event]);
}

export async function upsertEvents(events: UpsertEventParams[]) {
  try {
    await dispatchClient.put<UpsertEventParams>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/events`,
      events
    );

    return { success: true };
  } catch (error: any) {
    return { success: false, message: error.message || 'an error occurred' };
  }
}

export async function editEvent(event: UpsertEventParams) {
  try {
    await dispatchClient.put<UpsertEventParams>(
      `${APIM_SERVICE_RESOURCES_URL}/${event.resourceId}/events/${event.externalId}`,
      event
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

type SuperApiEvent = z.infer<typeof SuperApiTask>;
export async function rescheduleTask(
  event: Partial<SuperApiEvent>
): Promise<boolean> {
  invariant(event, 'Missing event, cannot update schedule');
  const res = await dispatchClient.post(
    `/dispatch/service/tasks/${event.taskReferenceId}/status/rescheduled`,
    {
      resourceId: event.resourceId,
      appointmentStart: event.startDate,
      appointmentEnd: event.endDate,
      reasonCode: event.reasonCode,
      isPrecise: false,
    }
  );
  return res.status === 200;
}

export async function setTaskUnassigned(
  taskId: string,
  currentUserEmail: string
): Promise<boolean | string> {
  invariant(taskId, 'Task ID is required to set task unassigned');

  try {
    const res = await dispatchClient.post(
      APIM_SERVICE_SET_TASK_UNASSIGNED(taskId),
      {},
      {
        headers: {
          'Sys-UserEmail': currentUserEmail,
        },
      }
    );

    return res.status === 200;
  } catch (error) {
    const errorResponse = error as AxiosError;

    throw new Error(errorResponse?.response?.data?.detail);
  }
}

export async function updateEvent(
  resourceId: string,
  startDateTime: Date | string,
  endDateTime: Date | string,
  reason: string,
  task?: Task,
  timeZone?: string
): Promise<boolean> {
  if (!task) return false;

  if (timeZone) {
    task.startDate = createDateWithTimeZoneOffset(
      startDateTime,
      timeZone
    ).toISOString();
    task.endDate = createDateWithTimeZoneOffset(
      endDateTime,
      timeZone
    ).toISOString();
  } else {
    task.startDate = new Date(startDateTime).toISOString();
    task.endDate = new Date(endDateTime).toISOString();
  }

  task.resourceId = resourceId;

  if (isEqual(new Date(task.startDate), new Date(task.endDate))) {
    task.endDate = addMinutes(new Date(endDateTime), 30).toISOString();
  }

  task.reasonCode = reason;

  return rescheduleTask(task);
}

export async function deleteEvent(data: {
  resourceId: string;
  eventId: string;
}) {
  try {
    await dispatchClient.delete(
      `${APIM_SERVICE_RESOURCES_URL}/${data?.resourceId}/events/${data?.eventId}`
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function upsertResource(resource: Resource) {
  try {
    await dispatchClient.put<Resource>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/resources/${resource.integrationGUID}`,
      resource
    );

    return { success: true };
  } catch (e) {
    return handleError(e as AxiosError | Error, 'Failed to create resource');
  }
}

export const handleError = (
  error: AxiosError | Error,
  defaultMessage: string
) => {
  if (axios.isAxiosError(error)) {
    return {
      success: false,
      message:
        error.response?.status === 400
          ? error.response?.data?.detail
          : defaultMessage,
    };
  }

  return { success: false, message: error?.message || defaultMessage };
};

export async function getEfficiencies(tenantId: string): Promise<number[]> {
  invariant(tenantId, 'Missing tenantId, cannot fetch efficiencies');
  const response = await dispatchClient.get<number[]>(
    `${APIM_SERVICE_EFFICIENCIES_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  const data = response?.data;

  return data;
}

export async function getStates(
  tenantId: string
): Promise<State[] | undefined> {
  invariant(tenantId, 'Missing tenantId, cannot fetch states options');

  const response = await dispatchClient.get<States[]>(
    `${APIM_SERVICE_STATES_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  const states: State[] = response?.data;

  return states;
}

export async function getLocationBasedEventsFlag(
  tenantId: string
): Promise<boolean | undefined> {
  try {
    const response = await dispatchClient.get<{
      showLocationBasedEvents: boolean;
    }>(`${APIM_LOCATION_BASED_EVENTS_URL(tenantId)}`, {
      headers: {
        'Request-ID': uuidv4(),
      },
    });

    const data = response?.data;

    if (response.status === 200 && data) {
      return data.showLocationBasedEvents;
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error('error fetching locationBasedEvents flag from API', e);
  }
  return undefined;
}

export async function getExcludeCertsAndLicenses(
  tenantId: string
): Promise<boolean | undefined> {
  try {
    const response = await dispatchClient.get<string>(
      `${APIM_SERVICE_EXCLUDE_CERTS_AND_LICENSES(tenantId)}`,
      {
        headers: {
          'Request-ID': uuidv4(),
        },
      }
    );

    const data = response?.data;

    if (response.status === 200 && data !== undefined && data !== null) {
      return String(data).toLowerCase() === 'true';
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error('error fetching excludeCertsAndLicenses flag from API', e);
  }
  return undefined;
}

export async function getRegionByPostalCode(
  postalCode: string,
  tenantId: string
): Promise<{
  data?: GeoResolverResponse;
  error?: GeoResolverError;
  status: number;
}> {
  try {
    const response = await dispatchClient.get<GeoResolverResponse>(
      APIM_SERVICE_GEO_REGIONS_URL(postalCode),
      {
        headers: {
          'Request-ID': uuidv4(),
          'Sys-TenantID': tenantId,
        },
      }
    );

    return {
      data: response.data,
      status: response.status,
    };
  } catch (error: any) {
    return {
      error: error?.response?.data,
      status: error?.response?.status || 500,
    };
  }
}

export async function getCountiesWithState(
  countyInput: string,
  stateInput?: string[]
): Promise<MultiSelectOption[] | undefined> {
  try {
    const response = await dispatchClient.post<Counties[]>(
      APIM_SERVICE_COUNTIES_URL,
      {
        county: countyInput,
        lossStates: stateInput,
      }
    );

    const data = response?.data;

    const counties: MultiSelectOption[] = data.map((county: Counties) => {
      return {
        title: `${county.state} - ${county.name}`,
        value: `${county.state} ^ ${county.name}`,
      };
    });

    if (response.status === 200 && data) {
      return counties;
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error('error fetching counties from API', e);
  }
  return undefined;
}

export const updateBulkResources = async (
  postBody: BulkUpdatePostBody
): Promise<{ success: boolean; message?: string | undefined }> => {
  try {
    const response = await dispatchClient.post<void>(
      APIM_SERVICE_RESOURCES_BULK_RESOURCES_URL,
      { ...postBody }
    );

    if (response.status === 200 || response.status === 204) {
      return { success: true };
    }

    return { success: false };
  } catch (e) {
    return { success: false };
  }
};

export const updateBulkResourcesAppend = async (
  postBody: BulkUpdatePostBody
): Promise<{ success: boolean; message?: string | undefined }> => {
  try {
    const response = await dispatchClient.post<void>(
      `${APIM_SERVICE_RESOURCES_BULK_RESOURCES_URL}`,
      { ...postBody },
      {
        params: {
          isAppend: true,
        },
      }
    );

    if (response.status === 200 || response.status === 204) {
      return { success: true };
    }

    return { success: false };
  } catch (e) {
    return { success: false };
  }
};

export async function getResourceFilters(userId: string) {
  try {
    const taskFilterResponse = await dispatchClient.get<ResourcesListFilter[]>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/resourceFilters`,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return taskFilterResponse?.data;
  } catch (e) {
    throw new Error('error fetching resource filters from API');
  }
  return undefined;
}

export async function upsertResourceFilters(
  filters: ResourcesListFilter,
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    const id = filters?.filterId || uuidv4();
    await dispatchClient.put<ResourcesListFilter>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/resourceFilters/${id}`,
      filters,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function deleteResourceFilter(userId: string, filterId: string) {
  return dispatchClient.delete(
    `${APIM_SERVICE_RESOURCE_SERVICE_URL}/resourceFilters/${filterId}`,
    {
      headers: {
        'Sys-UserID': userId,
      },
    }
  );
}

function cleanJSONArray(json: string) {
  return json.replace('""', '');
}

export async function getCapacityFilters(userId: string) {
  try {
    const taskFilterResponse = await dispatchClient.get<
      CapacityFilterResponse[]
    >(`${APIM_SERVICE_CAPACITY_FILTER_URL}`, {
      headers: {
        'Sys-UserID': userId,
      },
    });

    const response: CapacityFilter[] = taskFilterResponse?.data.map(
      (filter) => {
        return {
          ...filter,
          perilIds: filter?.perilIds ? JSON.parse(filter?.perilIds) : '',
          assignmentTypes: filter?.assignmentTypes
            ? JSON.parse(filter?.assignmentTypes)
            : [],
          roleIds: filter?.roleIds ? JSON.parse(filter?.roleIds) : [],
          resourceIds: filter?.resourceIds
            ? JSON.parse(filter?.resourceIds)
            : [],
          resourceCapacityPerDay: filter?.resourceCapacityPerDay
            ? JSON.parse(filter?.resourceCapacityPerDay)
            : [],
          locations: filter?.locations ? JSON.parse(filter?.locations) : [],
          teamIds: filter?.teamIds
            ? JSON.parse(cleanJSONArray(filter?.teamIds))
            : [],
          officeIds: filter?.officeIds ? JSON.parse(filter?.officeIds) : [],
          resourceAttributes: filter?.resourceAttributes
            ? JSON.parse(filter?.resourceAttributes)
            : [],
        };
      }
    );

    return response;
  } catch (e) {
    throw new Error('error fetching capacity filters from API');
  }
}

export async function upsertCapacityFilter(
  filters: CapacityFilterResponse,
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    const id = filters?.filterId || uuidv4();
    await dispatchClient.put<CapacityFilterResponse>(
      `${APIM_SERVICE_CAPACITY_FILTER_URL}/${id}`,
      filters,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function deleteCapacityFilter(userId: string, filterId: string) {
  return dispatchClient.delete(
    `${APIM_SERVICE_CAPACITY_FILTER_URL}/${filterId}`,
    {
      headers: {
        'Sys-UserID': userId,
      },
    }
  );
}

export async function getQualifiedResourcesPost(
  filters?: QualifiedResourcesFNOLFilters,
  token?: string
): Promise<ApiResponseWithPaging<QualifiedResourcePost[]>> {
  const response = await dispatchClient.post<QualifiedResourcePost[]>(
    `${APIM_SERVICE_QUALIFIED_RESOURCES_POST}`,
    filters,
    {
      headers: {
        'Continuation-Token': token || '',
      },
    }
  );

  return mapResponseWithPaging(response);
}

export async function orderIndexBulkResourceFilterUpdate(
  filters: (Omit<ResourcesListFilter, 'pageSize'> & { isNew?: boolean })[],
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    await dispatchClient.put<ResourcesFilters[]>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/filterOrderIndexBulkUpdate`,
      filters,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function groupsOrderIndexBulkResourceFilterUpdate(
  groups: ResourceFilterGroup[],
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    await dispatchClient.put<ResourceFilterGroup[]>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/groupOrderIndexBulkUpdate`,
      groups,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function upsertResourceFilterGroup(
  data: ResourceFilterGroup,
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    const id = data?.groupId;
    data.userId = userId;
    await dispatchClient.put<ResourceFilterGroup>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/resourcefilters/group/${id}`,
      data,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function deleteResourceFilterGroup(
  userId: string,
  groupId: string
) {
  return dispatchClient.delete(
    `${APIM_SERVICE_RESOURCE_SERVICE_URL}/resourcefilters/group/${groupId}`,
    {
      headers: {
        'Sys-UserID': userId,
      },
    }
  );
}

export async function getResourceFilterGroups(userId: string) {
  try {
    const resourceFilterResponse = await dispatchClient.get<any>(
      `${APIM_SERVICE_RESOURCE_SERVICE_URL}/groups`,
      {
        headers: {
          'Sys-UserID': userId, // will go away when api is updated
        },
      }
    );

    const resourceFilterGroupData = resourceFilterResponse?.data;

    if (!resourceFilterGroupData) {
      return undefined;
    } else if (
      resourceFilterResponse.status === 200 &&
      resourceFilterGroupData
    ) {
      return resourceFilterGroupData;
    }
  } catch (e) {
    throw new Error('error fetching task filters from API');
  }
  return undefined;
}
